import { initializeApp } from "firebase/app";
import { getFirestore, collection, addDoc, getDocs, doc, deleteDoc, updateDoc, getDoc, query, where, orderBy, limit } from 'firebase/firestore';
import { getAnalytics } from "firebase/analytics";
import { Hotel } from '../models/Hotel';
import { UserRole } from '../models/User';

// Firebase yapılandırması - Test Environment
const firebaseConfig = {
  apiKey: "AIzaSyDsv0xjYi-eVdBAjn0wkcpOXbbDDiu1cNc",
  authDomain: "ses-test-server.firebaseapp.com",
  projectId: "ses-test-server",
  storageBucket: "ses-test-server.firebasestorage.app",
  messagingSenderId: "882460515585",
  appId: "1:882460515585:web:5ee9d55e91e09cf0cc4f00",
  measurementId: "G-TERPYB7HLY"
};

// Firebase başlatma
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);
const db = getFirestore(app);

// Koleksiyon referansları
const hotelsCollection = collection(db, 'hotels');
const hotelUsersCollection = collection(db, 'hotelUsers');
const auditLogsCollection = collection(db, 'auditLogs');

/**
 * Firebase Hotel Interface
 */
export interface FirebaseHotel {
  id?: string;
  name: string;
  address: string;
  serverUrl: string;
  serverPort?: number;
  hotelCode?: string; // 6-digit unique code
  hotelInstanceId?: number; // Backend veri tabanındaki ID
  status?: 'online' | 'offline';
  lastChecked?: Date;
}

/**
 * Firebase Hotel User Interface
 */
export interface FirebaseHotelUser {
  id?: string;
  username: string;
  hotelId: string; // Firebase hotel referansı
  backendHotelId: number; // Backend hotel ID
  role: string; // 'HotelManager', 'HotelUser'
  createdAt: Date;
}


/**
 * Firebase'den tüm otelleri getir
 */
export const getFirebaseHotels = async (): Promise<FirebaseHotel[]> => {
  try {
    const snapshot = await getDocs(hotelsCollection);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name,
        address: data.address,
        serverUrl: data.serverUrl,
        serverPort: data.serverPort,
        hotelCode: data.hotelCode,
        hotelInstanceId: data.hotelInstanceId,
        status: data.status || 'offline',
        lastChecked: data.lastChecked?.toDate()
      } as FirebaseHotel;
    });
  } catch (error) {
    console.error("Error fetching hotels from Firebase:", error);
    return [];
  }
};

/**
 * ID ile Firebase'den otel getir
 */
export const getFirebaseHotelById = async (id: string): Promise<FirebaseHotel | null> => {
  try {
    const docRef = doc(db, 'hotels', id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }
    
    const data = docSnap.data();
    return {
      id: docSnap.id,
      name: data.name,
      address: data.address,
      serverUrl: data.serverUrl,
      serverPort: data.serverPort,
      hotelInstanceId: data.hotelInstanceId,
      status: data.status || 'offline',
      lastChecked: data.lastChecked?.toDate()
    } as FirebaseHotel;
  } catch (error) {
    console.error("Error fetching hotel by ID from Firebase:", error);
    return null;
  }
};

/**
 * Backend Hotel ID'si ile Firebase'den otel getir
 */
export const getFirebaseHotelByBackendId = async (backendId: number): Promise<FirebaseHotel | null> => {
  try {
    const q = query(hotelsCollection, where("hotelInstanceId", "==", backendId));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }
    
    const doc = snapshot.docs[0];
    const data = doc.data();
    return {
      id: doc.id,
      name: data.name,
      address: data.address,
      serverUrl: data.serverUrl,
      serverPort: data.serverPort,
      hotelInstanceId: data.hotelInstanceId,
      status: data.status || 'offline',
      lastChecked: data.lastChecked?.toDate()
    } as FirebaseHotel;
  } catch (error) {
    console.error("Error fetching hotel by backend ID from Firebase:", error);
    return null;
  }
};

/**
 * Firebase'e yeni otel ekle
 */
export const addHotelToFirebase = async (hotel: Omit<FirebaseHotel, 'id'>): Promise<string> => {
  try {
    const docRef = await addDoc(hotelsCollection, {
      ...hotel,
      status: 'offline',
      lastChecked: new Date()
    });
    return docRef.id;
  } catch (error) {
    console.error("Error adding hotel to Firebase:", error);
    throw error;
  }
};

/**
 * Firebase'deki oteli güncelle
 */
export const updateHotelInFirebase = async (id: string, hotel: Partial<FirebaseHotel>): Promise<void> => {
  try {
    const hotelRef = doc(db, 'hotels', id);
    
    // Sadece date alanları varsa, onları update etmeden önce kontrol et
    const updateData = { ...hotel };
    if (updateData.lastChecked && !(updateData.lastChecked instanceof Date)) {
      updateData.lastChecked = new Date(updateData.lastChecked);
    }
    
    await updateDoc(hotelRef, updateData);
  } catch (error) {
    console.error("Error updating hotel in Firebase:", error);
    throw error;
  }
};

/**
 * Firebase'den otel sil
 */
export const deleteHotelFromFirebase = async (id: string): Promise<void> => {
  try {
    // İlk olarak bu otele bağlı kullanıcıları sil
    const userQuery = query(hotelUsersCollection, where("hotelId", "==", id));
    const userSnapshot = await getDocs(userQuery);
    
    const deletePromises = userSnapshot.docs.map(userDoc => 
      deleteDoc(doc(db, 'hotelUsers', userDoc.id))
    );
    
    await Promise.all(deletePromises);
    
    // Sonra oteli sil
    const hotelRef = doc(db, 'hotels', id);
    await deleteDoc(hotelRef);
  } catch (error) {
    console.error("Error deleting hotel from Firebase:", error);
    throw error;
  }
};

/**
 * Otel durum kontrolü
 */
export const checkHotelStatus = async (hotel: FirebaseHotel): Promise<'online' | 'offline'> => {
  try {
    if (!hotel || !hotel.serverUrl) {
      console.warn("Invalid hotel data for status check", hotel);
      return 'offline';
    }
    
    // API endpoint oluştur - URL boş olamaz
    if (!hotel.serverUrl) {
      console.warn("Invalid server URL for status check", hotel.serverUrl);
      return 'offline';
    }

    const protocol = hotel.serverUrl.startsWith('http') ? '' : 'http://';
    const url = `${protocol}${hotel.serverUrl}${hotel.serverPort ? `:${hotel.serverPort}` : ''}/api/status`;
    
    console.log(`Checking hotel status at: ${url}`);
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        // Kısa timeout - 3 saniye içinde cevap vermeyen sunucular offline kabul edilir
        signal: AbortSignal.timeout(3000)
      });
      
      if (response.ok) {
        // Otel durumunu Firebase'de güncelle
        if (hotel.id) {
          await updateHotelInFirebase(hotel.id, { 
            status: 'online',
            lastChecked: new Date()
          });
        }
        return 'online';
      } else {
        if (hotel.id) {
          await updateHotelInFirebase(hotel.id, {
            status: 'offline',
            lastChecked: new Date()
          });
        }
        return 'offline';
      }
    } catch (error) {
      // Bağlantı hatası - offline
      console.error(`Error connecting to hotel ${hotel?.name || 'unknown'}:`, error);
      if (hotel?.id) {
        await updateHotelInFirebase(hotel.id, {
          status: 'offline',
          lastChecked: new Date()
        });
      }
      return 'offline';
    }
  } catch (error) {
    console.error(`Error in checkHotelStatus for hotel ${hotel?.name || 'unknown'}:`, error);
    if (hotel?.id) {
      await updateHotelInFirebase(hotel.id, {
        status: 'offline',
        lastChecked: new Date()
      });
    }
    return 'offline';
  }
};

/**
 * Backend modelini Firebase modeline dönüştür
 */
export const convertToFirebaseHotel = (hotel: Hotel): Omit<FirebaseHotel, 'id'> => {
  return {
    name: hotel.name,
    address: hotel.address,
    serverUrl: hotel.serverUrl,
    serverPort: hotel.serverPort,
    hotelInstanceId: hotel.id,
    status: 'offline',
  };
};

/**
 * Firebase modelini backend modeline dönüştür
 */
export const convertToBackendHotel = (hotel: FirebaseHotel): Partial<Hotel> => {
  return {
    id: hotel.hotelInstanceId,
    name: hotel.name,
    hotelCode: hotel.hotelCode,
    address: hotel.address,
    serverUrl: hotel.serverUrl,
    serverPort: hotel.serverPort,
    // Backend'in bekleyeceği diğer alanlar
    zoneCount: 0,
    userCount: 0
  };
};


/**
 * Kullanıcıyı Firebase'e ekle
 */
export const addUserToFirebase = async (
  username: string,
  firebaseHotelId: string,
  backendHotelId: number,
  role: UserRole
): Promise<string> => {
  try {
    const userRole = UserRole[role]; // Enum değerini string'e dönüştür
    
    const docRef = await addDoc(hotelUsersCollection, {
      username,
      hotelId: firebaseHotelId,
      backendHotelId,
      role: userRole,
      createdAt: new Date()
    });
    
    return docRef.id;
  } catch (error) {
    console.error("Error adding user to Firebase:", error);
    throw error;
  }
};

/**
 * Firebase'den otelin kullanıcılarını getir
 */
export const getUsersByHotel = async (hotelId: string): Promise<FirebaseHotelUser[]> => {
  try {
    const q = query(hotelUsersCollection, where("hotelId", "==", hotelId));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        username: data.username,
        hotelId: data.hotelId,
        backendHotelId: data.backendHotelId,
        role: data.role,
        createdAt: data.createdAt?.toDate()
      } as FirebaseHotelUser;
    });
  } catch (error) {
    console.error("Error fetching users by hotel from Firebase:", error);
    return [];
  }
};

/**
 * Firebase'deki kullanıcıyı güncelle
 */
export const updateUserInFirebase = async (
  username: string,
  backendHotelId: number,
  newUsername?: string,
  newRole?: string
): Promise<void> => {
  try {
    // Username ve backend hotel ID ile kullanıcıyı bul
    const q = query(
      hotelUsersCollection, 
      where("username", "==", username),
      where("backendHotelId", "==", backendHotelId)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      console.warn(`Firebase user not found: ${username} in hotel ${backendHotelId}`);
      return;
    }
    
    const userDoc = snapshot.docs[0];
    const updateData: any = {};
    
    if (newUsername && newUsername !== username) {
      updateData.username = newUsername;
    }
    
    if (newRole) {
      updateData.role = newRole;
    }
    
    if (Object.keys(updateData).length > 0) {
      await updateDoc(doc(db, 'hotelUsers', userDoc.id), updateData);
      console.log(`Firebase user updated: ${username} -> ${newUsername || username}`);
    }
  } catch (error) {
    console.error("Error updating user in Firebase:", error);
    throw error;
  }
};

/**
 * Firebase'den kullanıcıyı sil
 */
export const deleteUserFromFirebase = async (
  username: string,
  backendHotelId: number
): Promise<void> => {
  try {
    // Username ve backend hotel ID ile kullanıcıyı bul
    const q = query(
      hotelUsersCollection,
      where("username", "==", username),
      where("backendHotelId", "==", backendHotelId)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      console.warn(`Firebase user not found for deletion: ${username} in hotel ${backendHotelId}`);
      return;
    }
    
    // Tüm eşleşen kullanıcıları sil (normalde bir tane olmalı)
    const deletePromises = snapshot.docs.map(userDoc => 
      deleteDoc(doc(db, 'hotelUsers', userDoc.id))
    );
    
    await Promise.all(deletePromises);
    console.log(`Firebase user deleted: ${username} from hotel ${backendHotelId}`);
  } catch (error) {
    console.error("Error deleting user from Firebase:", error);
    throw error;
  }
};

/**
 * Firebase otel kontrolü - eğer yoksa ekle
 * Backend ve Firebase senkronizasyonu için kullanılır
 */
export const syncHotelWithFirebase = async (backendHotel: Hotel): Promise<FirebaseHotel> => {
  try {
    // Önce backend ID'si ile Firebase'de ara
    const existingHotel = await getFirebaseHotelByBackendId(backendHotel.id);
    
    // Eğer Firebase'de varsa güncelle ve döndür
    if (existingHotel && existingHotel.id) {
      const updatedData = convertToFirebaseHotel(backendHotel);
      await updateHotelInFirebase(existingHotel.id, updatedData);
      
      return {
        ...updatedData,
        id: existingHotel.id,
        // Mevcut durumu koru
        status: existingHotel.status || 'offline',
        lastChecked: existingHotel.lastChecked || new Date()
      };
    }
    
    // Yoksa ekle ve döndür
    const firebaseHotel = convertToFirebaseHotel(backendHotel);
    const newId = await addHotelToFirebase(firebaseHotel);
    
    return {
      ...firebaseHotel,
      id: newId,
      status: 'offline',
      lastChecked: new Date()
    };
  } catch (error) {
    console.error("Error syncing hotel with Firebase:", error);
    throw error;
  }
};


/**
 * Firebase Audit Log Interface
 */
export interface FirebaseAuditLog {
  id?: string;
  timestamp: Date;
  hotelId: number;
  userId: number;
  username: string;
  action: string;
  actionType: string;
  zoneId?: number;
  zoneName?: string;
  previousValue?: string;
  newValue?: string;
  additionalInfo?: string;
  ipAddress?: string;
  success: boolean;
  formattedMessage: string;
}

/**
 * Audit log yazma - kullanıcı işlemleri için
 */
export const logUserAction = async (auditLog: Omit<FirebaseAuditLog, 'id' | 'timestamp'>): Promise<string> => {
  try {
    const docRef = await addDoc(auditLogsCollection, {
      timestamp: new Date(),
      category: 'UserAction',
      actorType: 'User',
      hotelId: auditLog.hotelId,
      userId: auditLog.userId,
      username: auditLog.username,
      action: auditLog.action,
      actionType: auditLog.actionType,
      zoneId: auditLog.zoneId ?? null,
      zoneName: auditLog.zoneName ?? null,
      previousValue: auditLog.previousValue ?? null,
      newValue: auditLog.newValue ?? null,
      additionalInfo: auditLog.additionalInfo ?? null,
      ipAddress: auditLog.ipAddress ?? null,
      success: auditLog.success,
      formattedMessage: auditLog.formattedMessage
    });
    return docRef.id;
  } catch (error) {
    console.error("Error logging user action to Firebase:", error);
    throw error;
  }
};

/**
 * Audit logları getirme - tarihli
 */
export const getAuditLogs = async (hotelId: number, limitCount: number = 100): Promise<FirebaseAuditLog[]> => {
  try {
    const q = query(
      auditLogsCollection,
      where('hotelId', '==', hotelId),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        timestamp: data.timestamp?.toDate(),
        userId: data.userId,
        username: data.username,
        action: data.action,
        actionType: data.actionType,
        zoneId: data.zoneId,
        zoneName: data.zoneName,
        previousValue: data.previousValue,
        newValue: data.newValue,
        additionalInfo: data.additionalInfo,
        ipAddress: data.ipAddress,
        success: data.success,
        formattedMessage: data.formattedMessage
      } as FirebaseAuditLog;
    });
  } catch (error) {
    console.error("Error fetching audit logs from Firebase:", error);
    return [];
  }
};

/**
 * Kullanıcıya özel audit logları
 */
export const getUserAuditLogs = async (hotelId: number, userId: number, limitCount: number = 50): Promise<FirebaseAuditLog[]> => {
  try {
    const q = query(
      auditLogsCollection,
      where('hotelId', '==', hotelId),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        timestamp: data.timestamp?.toDate(),
        userId: data.userId,
        username: data.username,
        action: data.action,
        actionType: data.actionType,
        zoneId: data.zoneId,
        zoneName: data.zoneName,
        previousValue: data.previousValue,
        newValue: data.newValue,
        additionalInfo: data.additionalInfo,
        ipAddress: data.ipAddress,
        success: data.success,
        formattedMessage: data.formattedMessage
      } as FirebaseAuditLog;
    });
  } catch (error) {
    console.error("Error fetching user audit logs from Firebase:", error);
    return [];
  }
};

// ============= SUBSCRIPTION SERVICES (Webapp) =============

/**
 * Subscription Service for Webapp
 */
export class SubscriptionService {
  /**
   * Check subscription status by hotel code
   */
  static async checkSubscriptionByHotelCode(hotelCode: string): Promise<{ 
    status: 'active' | 'expired' | 'past_due' | 'not_found',
    daysLeft?: number,
    minutesLeft?: number,
    plan?: 'trial' | 'paid'
  }> {
    try {
      console.log('Checking subscription for hotel code:', hotelCode);
      
      // Find hotel by code
      const hotelQuery = query(hotelsCollection, where('hotelCode', '==', hotelCode));
      const hotelSnapshot = await getDocs(hotelQuery);
      
      if (hotelSnapshot.empty) {
        console.log('Hotel not found for code:', hotelCode);
        return { status: 'not_found' };
      }

      // Find webUser who owns this hotel
      const webUsersCollection = collection(db, 'webUsers');
      const webUserQuery = query(webUsersCollection, where('ownedHotels', 'array-contains', hotelCode));
      const userSnapshot = await getDocs(webUserQuery);
      
      if (userSnapshot.empty) {
        console.log('WebUser not found for hotel code:', hotelCode);
        return { status: 'not_found' };
      }

      const webUserData = userSnapshot.docs[0].data();
      const webUserId = userSnapshot.docs[0].id;
      
      console.log('Found webUser:', webUserId, 'for hotel:', hotelCode);
      
      // Get subscription
      const subscriptionsCollection = collection(db, 'subscriptions');
      const subscriptionQuery = query(subscriptionsCollection, where('webUserId', '==', webUserId));
      const subscriptionSnapshot = await getDocs(subscriptionQuery);
      
      if (subscriptionSnapshot.empty) {
        console.log('Subscription not found for user:', webUserId);
        return { status: 'not_found' };
      }

      const subscriptionData = subscriptionSnapshot.docs[0].data();
      const subscriptionDoc = subscriptionSnapshot.docs[0];
      
      console.log('Found subscription:', subscriptionData);
      
      const now = new Date();
      
      // Determine end date based on plan
      let endDate;
      if (subscriptionData.plan === 'trial' && subscriptionData.trialEndsAt) {
        endDate = subscriptionData.trialEndsAt.toDate ? subscriptionData.trialEndsAt.toDate() : new Date(subscriptionData.trialEndsAt);
      } else if (subscriptionData.currentPeriodEnd) {
        endDate = subscriptionData.currentPeriodEnd.toDate ? subscriptionData.currentPeriodEnd.toDate() : new Date(subscriptionData.currentPeriodEnd);
      }

      if (!endDate) {
        console.log('Subscription end date not found');
        return { status: 'expired' };
      }

      console.log('Subscription end date:', endDate.toISOString());
      console.log('Current time:', now.toISOString());

      if (endDate > now) {
        const timeDiff = endDate.getTime() - now.getTime();
        const daysLeft = Math.ceil(timeDiff / (24 * 60 * 60 * 1000));
        const minutesLeft = Math.ceil(timeDiff / (60 * 1000));
        const plan = subscriptionData.plan === 'trial' ? 'trial' : 'paid';
        
        console.log('Subscription active, days left:', daysLeft, 'minutes left:', minutesLeft, 'plan:', plan);
        return { 
          status: 'active', 
          daysLeft, 
          minutesLeft,
          plan
        };
      } else {
        console.log('Subscription expired');
        
        // Update subscription status to expired
        await updateDoc(subscriptionDoc.ref, {
          status: 'expired',
          updatedAt: new Date()
        });

        return { status: 'expired' };
      }
    } catch (error) {
      console.error('Error checking subscription by hotel code:', error);
      return { status: 'not_found' };
    }
  }
}

export default {
  getFirebaseHotels,
  getFirebaseHotelById,
  getFirebaseHotelByBackendId,
  addHotelToFirebase,
  updateHotelInFirebase,
  deleteHotelFromFirebase,
  checkHotelStatus,
  convertToFirebaseHotel,
  convertToBackendHotel,
  addUserToFirebase,
  getUsersByHotel,
  updateUserInFirebase,
  deleteUserFromFirebase,
  syncHotelWithFirebase,
  logUserAction,
  getAuditLogs,
  getUserAuditLogs
};